
import React from 'react';
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

interface IntegrationCardProps {
  title: string;
  description: string;
  logo: string;
  status: "connected" | "not_connected";
  onConnect: () => void;
}

const IntegrationCard = ({ title, description, logo, status, onConnect }: IntegrationCardProps) => {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center gap-4 pb-2">
        <img src={logo} alt={`${title} logo`} className="h-10 w-10 object-contain" />
        <div className="flex-1">
          <CardTitle className="text-lg">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        {status === "connected" ? (
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
            Connected
          </Badge>
        ) : null}
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground pb-2">
        {status === "connected" 
          ? "This integration is currently active and syncing data." 
          : "Connect this integration to sync data with your Drimt workspace."}
      </CardContent>
      <CardFooter>
        <Button 
          variant={status === "connected" ? "outline" : "default"} 
          className="w-full" 
          onClick={onConnect}
        >
          {status === "connected" ? "Manage Connection" : "Connect"}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default function Integrations() {
  const integrations = [
    {
      title: "Microsoft Azure 365",
      description: "Connect with Microsoft services and Office 365",
      logo: "/placeholder.svg",
      status: "not_connected" as const,
    },
    {
      title: "Google Workspace",
      description: "Sync with Google Drive, Calendar, and more",
      logo: "/placeholder.svg",
      status: "not_connected" as const,
    },
    {
      title: "Jira",
      description: "Track issues and manage project workflow",
      logo: "/placeholder.svg",
      status: "not_connected" as const,
    },
    {
      title: "Linear",
      description: "Issue tracking and project management for teams",
      logo: "/placeholder.svg",
      status: "not_connected" as const,
    },
    {
      title: "Slack",
      description: "Team communication and workspace collaboration",
      logo: "/placeholder.svg",
      status: "not_connected" as const,
    },
    {
      title: "Trello",
      description: "Visual project management and organization",
      logo: "/placeholder.svg",
      status: "not_connected" as const,
    },
    {
      title: "SAP",
      description: "Enterprise resource planning integration",
      logo: "/placeholder.svg",
      status: "not_connected" as const,
    },
    {
      title: "NetSuite",
      description: "Cloud-based business management",
      logo: "/placeholder.svg",
      status: "not_connected" as const,
    },
    {
      title: "Odoo",
      description: "Open source business apps",
      logo: "/placeholder.svg",
      status: "not_connected" as const,
    },
  ];

  const handleConnect = (integration: string) => {
    toast.info(`Connecting to ${integration}. Please wait...`);
    
    // Simulate API call
    setTimeout(() => {
      toast.success(`Successfully connected to ${integration}`);
    }, 1500);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Integrations</h1>
          <p className="text-muted-foreground mt-2">
            Connect Drimt with your favorite tools and services to streamline your workflow.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {integrations.map((integration) => (
            <IntegrationCard
              key={integration.title}
              title={integration.title}
              description={integration.description}
              logo={integration.logo}
              status={integration.status}
              onConnect={() => handleConnect(integration.title)}
            />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
